import { cloneElement, useRef } from 'react';
import type { ReactElement, ReactNode } from 'react';
import {
  useFloating, autoUpdate, offset, flip, shift, arrow,
  useClick, useDismiss, useRole, useInteractions,
  FloatingPortal, FloatingArrow, useTransitionStyles,
} from '@floating-ui/react';
import type { Placement } from '@floating-ui/react';
import './floating.scss';

// Replaces the @tippyjs/react "popper" variation. Controlled interactive menu:
// the consumer owns `open`. Closes on outside-press, ancestor scroll and Esc
// (replaces tippy's manual hide-on-scroll logic). `popperClassName` lands on the
// floating root (replaces instance.popper). Light theme (the only theme the
// popper menus used).

export interface PopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  content: ReactNode;
  placement?: Placement;
  popperClassName?: string;
  /**
   * 'click' (default): clicking the anchor toggles the menu (useClick).
   * 'manual': the consumer fully owns `open` (no anchor-click trigger) — used
   * by SocialSharing, whose anchor click is conditional (hideShareButton).
   */
  trigger?: 'click' | 'manual';
  /** The anchor element (must forward ref + props). */
  children: ReactElement<Record<string, unknown>>;
}

const ARROW_HEIGHT = 7;
const GAP = 10;

export default function Popover({
  open, onOpenChange, content, placement = 'top', popperClassName, trigger = 'click', children,
}: PopoverProps) {
  const arrowRef = useRef<SVGSVGElement>(null);

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset(ARROW_HEIGHT + GAP), flip(), shift({ padding: 8 }), arrow({ element: arrowRef })],
  });

  const click = useClick(context, { enabled: trigger === 'click' });
  const dismiss = useDismiss(context, { outsidePress: true, ancestorScroll: true, escapeKey: true });
  const role = useRole(context, { role: 'menu' });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: { open: 150, close: 100 },
    initial: { opacity: 0, transform: 'scale(0.96)' },
  });

  return (
    <>
      {cloneElement(
        children,
        // Spread the child's own props first, then floating's ref last so a
        // child that carries its own ref cannot clobber refs.setReference.
        getReferenceProps({ ...children.props, ref: refs.setReference })
      )}
      {isMounted && (
        <FloatingPortal>
          <div
              ref={refs.setFloating}
              style={{ ...floatingStyles, zIndex: 9999 }}
              className={popperClassName}
              {...getFloatingProps()}>
            <div className="sh-floating sh-floating--light" style={transitionStyles}>
              <div className="sh-floating__content">{content}</div>
              <FloatingArrow
                  ref={arrowRef}
                  context={context}
                  className="sh-floating__arrow sh-floating__arrow--light"
                  height={ARROW_HEIGHT} />
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
